import { useState, type CSSProperties } from 'react';
import { MOOD_LIST, MOODS, type MoodId } from '@racksavant/core';
import { cx } from '../cx';
import s from './MoodRail.module.css';

/**
 * The mood taxonomy, as a contact sheet.
 *
 * Fifteen columns, each one a swatch, an index, a name and a blurb. The emoji
 * chips are gone: a mood is a colour and a word, and the colours are content —
 * they are allowed to be loud against the ink and the bone.
 */
export function MoodRail() {
  const [active, setActive] = useState<MoodId>('soft');
  const mood = MOODS[active];

  return (
    <div className={s.rail} style={{ '--rail-color': mood.color } as CSSProperties}>
      <div className={s.readout} aria-live="polite">
        <p className={s.readoutName}>{mood.label}</p>
        <p className={s.readoutBlurb}>{mood.blurb}</p>
        <p className={s.readoutLight}>{mood.lightingHint}</p>
      </div>

      <div className={s.scrollerWrap}>
        <ul className={s.scroller}>
          {MOOD_LIST.map((m, i) => (
            <li key={m.id} className={s.item}>
              <button
                type="button"
                className={cx(s.col, m.id === active && s.colActive)}
                style={{ '--chip-color': m.color } as CSSProperties}
                aria-pressed={m.id === active}
                onMouseEnter={() => setActive(m.id)}
                onFocus={() => setActive(m.id)}
                onClick={() => setActive(m.id)}
              >
                <span className={s.swatch} aria-hidden="true" />
                <span className={s.index} aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className={s.name}>{m.label}</span>
                <span className={s.blurb}>{m.blurb}</span>
                {/* ARIA does not name role="generic", so the label went nowhere
                    and the rating was invisible. Real text, visually hidden. */}
                <span className={s.energy} aria-hidden="true">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <span key={n} className={cx(s.pip, n <= m.energy && s.pipOn)} />
                  ))}
                </span>
                <span className="rs-u-sr-only">Energy {m.energy} of 5</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <p className={s.hint}>{MOOD_LIST.length} of them. Scroll.</p>
    </div>
  );
}
